import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ValueType } from 'react-select';

import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';

import { IOption } from '~/types';
import { ITeamMemberMapped } from '~/utils/clientAllocation';
import { ClientWithAllocation, IEditAllocationFormValues } from '../../../shared/model/types';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import { normalizeClientOptionsToAllocations } from '../../utils';

import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import useSelectAdditionalStyles from '../../hooks/useSelectAdditionalStyles';

interface IProps {
  teamMember: ITeamMemberMapped;
  teamMemberIdx: number;
  updateAllocations: (selectedClients: ClientWithAllocation[]) => void;
}

const AllocationClientsSelect: FC<IProps> = ({
  teamMember,
  teamMemberIdx,
  updateAllocations,
}): ReactElement => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<IEditAllocationFormValues>();
  const { clients } = useEditClientAllocationContext();
  const { markChanged, markDeleted, unmarkDeleted } =
    useAllocationChangeTracking<IEditAllocationFormValues>({
      changedField: 'changedAllocationsIds',
      deletedField: 'deletedAllocationsIds',
    });

  const selectedFieldName = `teamMembersAllocations.${teamMemberIdx}.selectedClientsIds` as const;

  const { additionalStyleHandler } = useSelectAdditionalStyles(teamMemberIdx);

  const teamMembersAllocations = watch('teamMembersAllocations');

  const clientsAllocationsFields = useMemo(
    () => teamMembersAllocations[teamMemberIdx].clientsAllocations,
    [teamMembersAllocations, teamMemberIdx],
  );

  const groupedExistingClientsByClientId = useMemo(
    () => new Map(clientsAllocationsFields.map(field => [field.clientId, field])),
    [clientsAllocationsFields],
  );

  const trackAllocationChange = useCallback(
    (existedAllocationId: number | null | undefined, hasOptions: boolean) => {
      if (!existedAllocationId) return;
      if (hasOptions) {
        unmarkDeleted(existedAllocationId);
        markChanged(existedAllocationId);
        return;
      }

      markDeleted(existedAllocationId);
    },
    [markChanged, markDeleted, unmarkDeleted],
  );

  const handleSelectClientOptions = useCallback(
    (selected: ValueType<IOption | IOption[], boolean>) => {
      const options = (Array.isArray(selected) ? selected : []) as IOption[];
      const existedAllocationId =
        clientsAllocationsFields.find(alloc => alloc.allocation?.id)?.allocation.id ?? null;

      trackAllocationChange(existedAllocationId, options.length > 0);

      updateAllocations(
        normalizeClientOptionsToAllocations(options, existedAllocationId).map(
          client => groupedExistingClientsByClientId.get(client.clientId) ?? client,
        ),
      );
    },
    [
      clientsAllocationsFields,
      groupedExistingClientsByClientId,
      trackAllocationChange,
      updateAllocations,
    ],
  );

  return (
    <SelectComponent
      name={selectedFieldName}
      control={control}
      errors={errors}
      closeMenuOnSelect={false}
      menuPortalTarget={null}
      menuPlacement="auto"
      isRelativeWindow={false}
      showCustomComponents
      hideSelectedOptions={false}
      hideMultiValueRemove
      additionalStyleHandler={additionalStyleHandler}
      isMulti
      label="Select Clients to Assign"
      options={clients}
      disableMenu={teamMember.isDeleted}
      onSelect={handleSelectClientOptions}
    />
  );
};

export default AllocationClientsSelect;
